import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Trash2, Send, MessageSquare } from "lucide-react";

export default function CommentsThread({ timelineId, eventId, canComment = true }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/timelines/${timelineId}/comments`, { params: eventId ? { event_id: eventId } : {} });
      setItems(data);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [timelineId, eventId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/timelines/${timelineId}/comments`, { event_id: eventId || "", content: text });
      setItems((p) => [...p, data]);
      setText("");
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/comments/${id}`); setItems((p) => p.filter((c) => c.id !== id)); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="space-y-3" data-testid="comments-thread">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
        <MessageSquare size={13} /> {items.length} comment{items.length === 1 ? "" : "s"}
      </div>
      <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
        {items.length === 0 && <p className="text-sm text-muted-foreground">No comments yet. Start the conversation.</p>}
        {items.map((c) => (
          <div key={c.id} className="flex gap-2.5 group" data-testid={`comment-${c.id}`}>
            <Avatar className="h-7 w-7 shrink-0 mt-0.5"><AvatarFallback className="bg-primary text-primary-foreground text-[11px]">{(c.user_name || "U").slice(0, 1).toUpperCase()}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">{c.user_name}</span>
                <span className="text-[11px] text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</span>
                {user && (user.id === c.user_id) && (
                  <button onClick={() => del(c.id)} className="ml-auto opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"><Trash2 size={13} /></button>
                )}
              </div>
              <p className="text-sm text-foreground/80 break-words">{c.content}</p>
            </div>
          </div>
        ))}
      </div>
      {user ? (
        canComment && (
          <form onSubmit={submit} className="flex items-end gap-2 pt-1">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment…" rows={2} className="text-sm min-h-[38px]" data-testid="comment-input"
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e); }} />
            <Button type="submit" size="icon" disabled={busy || !text.trim()} className="rounded-full shrink-0" data-testid="comment-submit"><Send size={15} /></Button>
          </form>
        )
      ) : (
        <div className="text-sm text-muted-foreground pt-1"><Link to="/login" className="text-primary font-medium hover:underline">Log in</Link> to comment.</div>
      )}
    </div>
  );
}
